import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useState } from 'react';

export default function VolunteerRecruitmentPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showSafety, setShowSafety] = useState(false);

  const faqs = [
    {
      question: 'どれくらいの頻度で参加する必要がありますか？',
      answer: 'ご自身のペースで参加できます。通知のオン／オフで調整できるので、無理のない範囲で続けられます。'
    },
    {
      question: '顔出しは必要ですか？',
      answer: '基本は、顔出しでの対応をお願いしています（安心して学べる環境づくりのため）。※事情がある場合はご相談ください。'
    },
    {
      question: '初心者でも大丈夫ですか？',
      answer: '大丈夫です。こどもたちがアップした課題をAIが解析し、指導のヒント（進め方）を提示するので、初めての方でも取り組みやすい仕組みです。'
    },
    {
      question: 'どんな科目が多いですか？',
      answer: '学年や時期によってさまざまです。依頼検索で科目を絞り込めるので、得意な科目・対応しやすい内容を選んで参加できます。'
    },
    {
      question: '活動の雰囲気は？',
      answer: 'オンラインでの指導ですが、1対1のサポートや複数対応など形はさまざまです。実際の「こどもの居場所」の雰囲気を感じられる場面もあります。'
    },
    {
      question: '謝礼／謝金はありますか？',
      answer: 'あります。ボラタスは、謝金をお支払いして支援を継続しやすくすることを目的に作っています。安心して、こどもたちへの支援を続けてください。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-orange-50 via-amber-50 to-teal-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                <i className="ri-hand-heart-line text-4xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              ボランティア募集のお知らせ
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              オンラインで、こどもたちの学習を一緒に支えませんか。
            </p>
          </div>

          {/* 3点要約 */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-12 border-2 border-orange-100">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-smartphone-line text-xl text-white"></i>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed pt-1">
                  スマホやPCで参加できるオンライン学習サポート
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed pt-1">
                  こどもの個人情報に配慮した安心設計
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                  <i className="ri-team-line text-xl text-white"></i>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed pt-1">
                  初めての方も参加しやすいよう運営がサポート
                </p>
              </div>
            </div>

            {/* Main CTA */}
            <div className="mt-10 space-y-4">
              <a
                href="https://boratasu.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center bg-gradient-to-r from-orange-500 to-amber-500 text-white px-12 py-5 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line mr-2"></i>
                ボラタスで新規登録して、ボランティアを始める
              </a>
              <a
                href="/contact"
                className="block text-center border-2 border-orange-500 text-orange-600 px-12 py-4 rounded-xl font-semibold text-base hover:bg-orange-50 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-question-line mr-2"></i>
                迷っている方はお気軽にご相談ください
              </a>
              <p className="text-sm text-gray-500 text-center leading-relaxed">
                登録後の流れは、運営からご案内します。まずは登録だけでもOKです。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* どんな活動？ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            どんな活動？
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* やること */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 border-2 border-orange-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mr-4">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">やること</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-500 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700 leading-relaxed">こどもがアップした問題や質問のサポート</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-500 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700 leading-relaxed">わからない所を一緒に整理して、考え方を伝える</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-500 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700 leading-relaxed">「教える」より「寄り添う」ことを大切に</span>
                </li>
              </ul>
            </div>

            {/* こんな方におすすめ */}
            <div className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-3xl p-8 border-2 border-teal-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-orange-500 rounded-xl flex items-center justify-center mr-4">
                  <i className="ri-user-heart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">こんな方におすすめ</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-star-fill text-teal-500 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700 leading-relaxed">こどもの学びを応援したい</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-teal-500 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700 leading-relaxed">教育に関心がある（教職志望・学生・社会人歓迎）</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-teal-500 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700 leading-relaxed">まずは短時間から試したい</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 参加の流れ */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            参加の流れ
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* STEP 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-orange-100 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">ボラタスで新規登録</h3>
              <p className="text-gray-600 leading-relaxed">
                簡単な情報を入力
              </p>
            </div>

            {/* STEP 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-orange-100 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">案内・確認</h3>
              <p className="text-gray-600 leading-relaxed">
                運営から連絡
              </p>
            </div>

            {/* STEP 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-orange-100 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">活動スタート</h3>
              <p className="text-gray-600 leading-relaxed">
                無理のない範囲で
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 安心して参加できるために */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            安心して参加できるために
          </h2>
          <div className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-3xl p-8 border-2 border-teal-100">
            <button
              onClick={() => setShowSafety(!showSafety)}
              className="w-full flex items-center justify-between text-left cursor-pointer"
            >
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <i className="ri-shield-check-line text-teal-600 text-2xl mr-3"></i>
                安心・安全への取り組み
              </h3>
              <i className={`ri-arrow-${showSafety ? 'up' : 'down'}-s-line text-2xl text-gray-600 transition-transform`}></i>
            </button>
            {showSafety && (
              <div className="mt-6 space-y-4 text-gray-700 leading-relaxed">
                <div className="flex items-start">
                  <i className="ri-user-forbid-line text-teal-600 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <p>こどもの個人情報への配慮</p>
                </div>
                <div className="flex items-start">
                  <i className="ri-customer-service-2-line text-teal-600 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <p>困った時の相談先（運営がサポート）</p>
                </div>
                <div className="flex items-start">
                  <i className="ri-heart-3-line text-teal-600 text-xl mr-3 flex-shrink-0 mt-1"></i>
                  <p>参加時に気をつけたいポイント（上から目線にならない・安心安全）</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* よくある質問 */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            よくある質問
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full p-6 text-left flex items-start justify-between cursor-pointer hover:bg-orange-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-start">
                      <span className="inline-block bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold px-3 py-1 rounded-lg text-sm mr-3 flex-shrink-0">Q</span>
                      <h3 className="font-bold text-gray-900 leading-relaxed">{faq.question}</h3>
                    </div>
                  </div>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-gray-600 flex-shrink-0 ml-4 transition-transform`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <div className="flex items-start pt-4 border-t-2 border-gray-100">
                      <span className="inline-block bg-gradient-to-br from-teal-500 to-orange-500 text-white font-bold px-3 py-1 rounded-lg text-sm mr-3 flex-shrink-0">A</span>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-8 text-center leading-relaxed">
            ※謝金の条件（支払い方法・金額・対象）は、依頼内容や運用状況により異なる場合があります。詳細は登録後にご案内します。
          </p>
        </div>
      </section>

      {/* 最後に */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 to-amber-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="mb-8">
            <p className="text-xl md:text-2xl mb-4 leading-relaxed">
              できる範囲での参加を歓迎します
            </p>
            <p className="text-lg md:text-xl opacity-90 leading-relaxed">
              こどもたちの学びを支える輪を一緒に広げたい
            </p>
          </div>
          <div className="space-y-4">
            <a
              href="https://boratasu.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-orange-600 px-12 py-5 rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-add-line mr-2"></i>
              ボラタスで新規登録して、ボランティアを始める
            </a>
            <div>
              <a
                href="/contact"
                className="inline-block border-2 border-white text-white px-12 py-4 rounded-xl font-semibold text-base hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-question-line mr-2"></i>
                迷っている方はお気軽にご相談ください
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
